import { debounce, min } from 'lodash';
import { MAX_BATCH_SIZE, QUEUE_WAIT_TIME } from './queue-constants';
import type { RootStore } from '~/stores/root.store';
import { Action, ActionStatus } from './action';
import { cloneClass } from '~/utils/data.utils';
import { LogEvents } from '../fullstory/log-events';
import { isNetworkError } from '~/utils/network.utils';
import {
    RecoveryMode,
    RecoveryModeOptions,
    recoveryModeOptionsOptionsFactory
} from '~/services/actions-manager/recovery-mode';

export enum ActionType {
    apply = 'apply',
    undo = 'undo',
    redo = 'redo'
}

interface IServerRequestsQueueOptions {
    recoveryMode?: RecoveryMode;
    recoveryModeOptions?: RecoveryModeOptions;
}

const ServerRequestsQueueOptionsDefaults: IServerRequestsQueueOptions = {
    recoveryMode: RecoveryMode.none
};

type ISendActionsToServerProps = {
    actions: Action[];
    retryNumber?: number;
    retryDelay?: number;
};

export type TNetworkConnectivityIssueCallback = ({
    isStable
}: {
    isStable: boolean;
}) => void;

type AllActionFlushedCallbackProps = { success: boolean; error?: Error };

export class ServerRequestsQueue {
    _queue: Array<Action>;
    _offlineQueue: Array<Action>;
    onActionFailedCallbacks: Array<(action, error) => void>;
    onActionsSentCallbacks: Array<(actions: Array<Action>) => void>;
    onNetworkConnectivityIssueCallbacks: Array<TNetworkConnectivityIssueCallback>;
    onAllActionsFlushed: Array<(props: AllActionFlushedCallbackProps) => void> =
        [];

    currentRequest: Promise<Array<any>>;
    currentTimeoutId: any;

    recoveryMode: RecoveryMode;
    recoveryModeOptions: RecoveryModeOptions;

    rootStore: RootStore;

    executeNextBulkRequests: any;

    get arePendingActions() {
        return this._queue.length;
    }

    constructor(
        rootStore: RootStore,
        {
            recoveryMode,
            recoveryModeOptions
        }: IServerRequestsQueueOptions = ServerRequestsQueueOptionsDefaults
    ) {
        this.rootStore = rootStore;
        this.clear();

        this.recoveryMode =
            recoveryMode || ServerRequestsQueueOptionsDefaults.recoveryMode;
        this.recoveryModeOptions = recoveryModeOptionsOptionsFactory(
            this.recoveryMode,
            recoveryModeOptions
        );

        this.executeNextBulkRequests = debounce(
            () => this._executeNextBulkRequests(),
            QUEUE_WAIT_TIME
        );

        this.push = this.push.bind(this);
    }

    get queue() {
        return this._queue;
    }

    get isRequestInProgress() {
        return !!this.currentRequest;
    }

    get hasPendingChanges() {
        return (
            this.queue.length || this.currentTimeoutId || this.currentRequest
        );
    }

    push(action: Action, actionType: ActionType = ActionType.apply) {
        if (
            this.rootStore.networkStore.isOnline ||
            this.recoveryMode === RecoveryMode.retryOnNetworkError
        ) {
            this.pushOnline(action, actionType);

            return;
        }

        this.pushOffline(action, actionType);
    }

    private pushOnline(action: Action, actionType: ActionType) {
        if (this._queue.indexOf(action) !== -1) {
            return;
        }

        const newStatus = this.getStatus(actionType);
        action.setStatus(newStatus);

        const clonedAction = cloneClass(action) as Action;
        clonedAction.wasAddedToTheOfflineQueue = false;
        this._queue.push(clonedAction);

        this.rootStore.fullstoryService.debug(
            LogEvents.ActionPushedToServerRequestQueue(
                clonedAction.action_number,
                true
            )
        );

        this._tryExecuteNextBulkRequests();
    }

    pushOffline(action: Action, actionType: ActionType = ActionType.apply) {
        const lastAction = this._offlineQueue[this._offlineQueue.length - 1];

        const isUndoForUnresolvedAction =
            lastAction &&
            actionType === ActionType.undo &&
            (lastAction.status === ActionStatus.redoLocally ||
                lastAction.status === ActionStatus.appliedLocally);
        const isRedoForUnresolvedAction =
            lastAction &&
            actionType === ActionType.redo &&
            lastAction.status === ActionStatus.revertedLocally;

        if (
            lastAction &&
            action.action_number === lastAction.action_number &&
            (isUndoForUnresolvedAction || isRedoForUnresolvedAction)
        ) {
            action.setStatus(this.getStatus(actionType));

            this._offlineQueue.pop();

            return;
        }

        action.setStatus(this.getStatus(actionType));

        const clonedAction = cloneClass(action) as Action;
        clonedAction.wasAddedToTheOfflineQueue = true;
        this._offlineQueue.push(clonedAction);
        this.rootStore.fullstoryService.debug(
            LogEvents.ActionPushedToServerRequestQueue(
                clonedAction.action_number,
                false
            )
        );
    }

    private getStatus(actionType: ActionType): ActionStatus {
        if (actionType === ActionType.apply) {
            return ActionStatus.appliedLocally;
        }

        if (actionType === ActionType.undo) {
            return ActionStatus.revertedLocally;
        }

        if (actionType === ActionType.redo) {
            return ActionStatus.redoLocally;
        }

        throw new Error(`Unknown action type: ${actionType}`);
    }

    _tryExecuteNextBulkRequests() {
        if (!this.arePendingActions) {
            this.invokeAllActionsFlushed({ success: true });

            return;
        }

        if (this.arePendingActions >= MAX_BATCH_SIZE) {
            this.executeNextBulkRequests();
            this.executeNextBulkRequests.flush();

            return;
        }

        this.executeNextBulkRequests();
    }

    private async _executeNextBulkRequests() {
        const { networkStore } = this.rootStore;

        if (
            !networkStore.isOnline &&
            this.recoveryMode !== RecoveryMode.retryOnNetworkError
        ) {
            return;
        }

        if (!this.arePendingActions) {
            this.invokeAllActionsFlushed({ success: true });

            return;
        }

        if (this.currentRequest) {
            return;
        }

        const actionsToSend = this._queue
            .splice(0, MAX_BATCH_SIZE)
            .filter(x => x.isLocalStatus);

        await this.sendActionsToServer({
            actions: actionsToSend
        });
    }

    private async sendActionsToServer({
        actions,
        retryNumber = 0,
        retryDelay
    }: ISendActionsToServerProps) {
        const { fullstoryService, builderUiStore, flowCtrl, networkStore } =
            this.rootStore;

        try {
            this.currentRequest = flowCtrl.applyActions({
                flow: builderUiStore.selectedFlow,
                actions,
                retryNumber
            });

            fullstoryService.debug(
                LogEvents.ActionsNumbers(
                    `Flushing ${actions.length} actions numbers:`,
                    actions
                )
            );
            this.actionsSent(actions);

            await this.currentRequest;

            fullstoryService.debug(
                LogEvents.ActionsNumbers(
                    `Actions succeed on the server:`,
                    actions
                )
            );

            if (
                this.recoveryMode === RecoveryMode.retryOnNetworkError &&
                retryNumber >=
                    this.recoveryModeOptions
                        ?.informUnstableConnectionAfterRetries
            ) {
                this.networkConnectivityIssue({ isStable: true });
            }

            this.currentTimeoutId = setTimeout(() => {
                this.currentRequest = null;
                this._tryExecuteNextBulkRequests();
            }, 0);
        } catch (error) {
            if (this.recoveryMode === RecoveryMode.retryOnNetworkError) {
                if (isNetworkError(error)) {
                    this.retryActionsOnNetworkError({
                        actions,
                        retryNumber,
                        retryDelay
                    });

                    return;
                }

                this.currentRequest = null;

                if (!networkStore.isOnline) {
                    networkStore.setIsOnline(true);
                }
            }

            this.handleServerError(actions, error);
        }
    }

    private retryActionsOnNetworkError({
        actions,
        retryNumber,
        retryDelay
    }: ISendActionsToServerProps) {
        const { fullstoryService } = this.rootStore;

        const newRetryDelay = this.calculateNewRetryDelay(
            retryNumber,
            retryDelay
        );
        const newRetryNumber = retryNumber + 1;

        fullstoryService.debug(
            LogEvents.ActionsNumbers(
                `Try #${newRetryNumber}: Retrying in ${
                    newRetryDelay / 1000
                }s actions:`,
                actions
            )
        );

        if (
            retryNumber ===
            this.recoveryModeOptions.informUnstableConnectionAfterRetries
        ) {
            this.networkConnectivityIssue({ isStable: false });
        }

        this.currentTimeoutId = setTimeout(() => {
            this.sendActionsToServer({
                actions,
                retryNumber: newRetryNumber,
                retryDelay: newRetryDelay
            });
        }, newRetryDelay);
    }

    private handleServerError(actionsToSend: Action[], error) {
        this.actionsFailed(actionsToSend, error);

        this.invokeAllActionsFlushed({ success: false, error });
    }

    onActionsFailed(callback: (action, error) => void) {
        this.onActionFailedCallbacks.push(callback);
    }

    onActionsSent(callback: (action: Array<Action>) => void) {
        this.onActionsSentCallbacks.push(callback);
    }

    onNetworkConnectivityIssue(callback: TNetworkConnectivityIssueCallback) {
        this.onNetworkConnectivityIssueCallbacks.push(callback);
    }

    private actionsFailed(actions: Array<Action>, error) {
        this.rootStore.fullstoryService.error(error);

        this.onActionFailedCallbacks.forEach(callback =>
            callback(actions, error)
        );
    }

    private actionsSent(actions: Array<Action>) {
        this.onActionsSentCallbacks.forEach(callback => callback(actions));
    }

    private networkConnectivityIssue({ isStable }: { isStable: boolean }) {
        this.onNetworkConnectivityIssueCallbacks.forEach(callback =>
            callback({ isStable })
        );
    }

    clear() {
        this.clearQueue();
        this.onActionFailedCallbacks = [];
        this.onActionsSentCallbacks = [];
        this.onNetworkConnectivityIssueCallbacks = [];
        this.onAllActionsFlushed = [];
        this.executeNextBulkRequests?.cancel();
    }

    invokeAllActionsFlushed(props: AllActionFlushedCallbackProps) {
        if (this.onAllActionsFlushed?.length) {
            this.onAllActionsFlushed.forEach(cb => cb(props));
        }

        this.onAllActionsFlushed = [];
    }

    clearQueue() {
        this._queue = [];
        this._offlineQueue = [];
        this.clearRetryLoop();
    }

    clearRetryLoop() {
        if (!this.currentTimeoutId) {
            return;
        }

        clearTimeout(this.currentTimeoutId);
    }

    async flushRequests() {
        const { fullstoryService } = this.rootStore;

        this._offlineQueue.forEach(x => this._queue.push(x));
        const sentActions = this._offlineQueue;
        this._offlineQueue = [];

        fullstoryService.warn(
            LogEvents.ActionsNumbers(
                `Flushing all ${sentActions.length} remaining actions to the server:`,
                sentActions
            )
        );

        await this.onPostRequestsExecution();

        return sentActions;
    }

    async onPostRequestsExecution() {
        if (!this.isRequestInProgress && !this.arePendingActions) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            this.onAllActionsFlushed.push(
                ({ success, error }: { success: boolean; error: Error }) => {
                    if (success) {
                        resolve(null);

                        return;
                    }

                    reject(error);
                }
            );

            this.executeNextBulkRequests();
        });
    }

    private calculateNewRetryDelay(retryNumber: number, retryDelay: number) {
        if (
            !retryDelay ||
            !this.recoveryModeOptions.incrementalDelay ||
            retryNumber <
                this.recoveryModeOptions.incrementalDelay.startAfterRetries
        ) {
            return this.recoveryModeOptions.delay;
        }

        const { backoff, max } = this.recoveryModeOptions.incrementalDelay;

        return min([retryDelay + backoff, max]);
    }

    flushNow() {
        this.executeNextBulkRequests.flush();
    }
}
